import { baseApi } from '../../../core/baseApi';
import type {
  BillingAccount,
  UpdateBillingAccountBody,
  PaymentSetupResponse,
  ConfirmPaymentBody,
  ConfirmPaymentResponse,
  ToggleChargingBody,
  CancellationReason,
  ProcessAccountCancellationBody,
  CancellationResponse,
} from '../types';
import type { ApiResponse } from '../../../patterns/crud';

export const billingAccountApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    // Get billing account
    getBillingAccount: builder.query<BillingAccount, void>({
      query: () => '/billing/account',
      transformResponse: (response: ApiResponse<BillingAccount>) => response.data,
      providesTags: ['BillingAccount'],
    }),

    // Update billing account
    updateBillingAccount: builder.mutation<BillingAccount, UpdateBillingAccountBody>({
      query: (body) => ({
        url: '/billing/account',
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiResponse<BillingAccount>) => response.data,
      invalidatesTags: ['BillingAccount'],
    }),

    // Create payment setup (returns Stripe SetupIntent client_secret)
    createPaymentSetup: builder.mutation<PaymentSetupResponse, void>({
      query: () => ({
        url: '/billing/account/payment/setup',
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<PaymentSetupResponse>) => response.data,
    }),

    // Confirm payment setup after Stripe SetupIntent completes
    confirmPaymentSetup: builder.mutation<ConfirmPaymentResponse, ConfirmPaymentBody>({
      query: (body) => ({
        url: '/billing/account/payment/confirm',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<ConfirmPaymentResponse>) => response.data,
      invalidatesTags: ['BillingAccount'],
    }),

    // Toggle charging status
    toggleCharging: builder.mutation<{ isCharging: boolean }, ToggleChargingBody>({
      query: (body) => ({
        url: '/billing/account/charging',
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiResponse<{ isCharging: boolean }>) => response.data,
      invalidatesTags: ['BillingAccount'],
    }),

    // Get cancellation reason options
    getCancellationReasons: builder.query<CancellationReason[], void>({
      query: () => '/billing/cancellation/reasons',
      transformResponse: (response: ApiResponse<CancellationReason[]>) => response.data,
    }),

    // Cancel account (owner only)
    cancelAccount: builder.mutation<CancellationResponse & { message?: string }, ProcessAccountCancellationBody>({
      query: (body) => ({
        url: '/billing/cancellation',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<CancellationResponse> & { message?: string }) => ({
        cancellationId: response.data?.cancellationId,
        message: response.message,
      }),
      invalidatesTags: ['BillingAccount', { type: 'Subscriptions', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetBillingAccountQuery,
  useUpdateBillingAccountMutation,
  useCreatePaymentSetupMutation,
  useConfirmPaymentSetupMutation,
  useToggleChargingMutation,
  useGetCancellationReasonsQuery,
  useCancelAccountMutation,
} = billingAccountApi;
